import axios from '@/clientAPI.ts'
import axiosV3 from '@/clientAPIV3.ts'
import {User} from '@/features/authentication/types.ts'
import {GoalV3} from '@/features/chat/chat.model'
import {SupportedGoalsPropsType} from '../queries/useFetchSupportedGoals'
import * as types from '../types'
import {AttachmentFileType} from '@/features/chat/types'
import {PROOF_MODEL} from './goal.schema'

export const GOAL_API = {
    getGoalsList: async ({
        page,
        limit,
        typeId,
        sortParams,
        chat_topics_ids,
        search
    }: types.GetGoalsListRequest): Promise<types.ResponseActiveGoalsList> => {
        const params = {page, limit, type_id: typeId, chat_topics_ids: chat_topics_ids, search, ...sortParams}

        const {data} = await axios.get('/goals', {params})
        return data
    },
    getGoalsPaths: async ({name, typeId, sortParams}: types.GoalsPathsRequest): Promise<types.PathGoalsResponse> => {
        const {data} = await axios.get('/goals/chat-topics', {
            params: {name, type_id: typeId, ...sortParams}
        })
        return data
    },
    getSupporters: async (goalId: string): Promise<types.SupporterType[]> => {
        const {data} = await axios.get(`goals/${goalId}/supporters`)
        return data
    },
    getPastGoals: async ({
        page = 1,
        limit = 10,
        rookieId
    }: types.GetPastGoalsList): Promise<types.ResponseActiveGoalsList> => {
        const {data} = await axios.get(`/rookies/${rookieId}/goals/past`, {
            params: {page, limit}
        })
        return data
    },
    getSupportedGoalsList: async ({
        page = '1',
        limit = 10,
        sortParams,
        isActive
    }: SupportedGoalsPropsType): Promise<types.ResponseActiveGoalsList> => {
        const {data} = await axios.get('/goals/supported', {
            params: {page, limit, ...sortParams, is_active: isActive}
        })
        return data
    },
    getBroadcastsSupportersList: async (representedUserId?: number | string): Promise<User[]> => {
        const params = representedUserId
            ? {
                  represented_rookie_id: representedUserId
              }
            : undefined
        const {data} = await axios.get('/leader', {params})
        return data
    },
    createBroadcast: async (
        body: types.CreateBroadcast,
        representedUserId?: number | string
    ): Promise<types.Broadcast> => {
        const {data} = await axios.post('/broadcasts?response_type=extended', {
            ...body,
            represented_rookie_id: representedUserId
        })
        return data
    },
    addLeaderNickname: async ({data: body, leaderId}: types.AddLeaderNickname): Promise<types.NicknameOfLeader> => {
        const {data} = await axios.post(`/leader/${leaderId}/nickname`, body)
        return data
    },

    updateLeaderNickname: async ({
        data: body,
        leaderId,
        nicknameId
    }: types.UpdateLeaderNicknameRequest): Promise<types.NicknameOfLeader> => {
        const {data} = await axios.patch(`/leader/${leaderId}/nickname/${nicknameId}`, body)
        return data
    },
    deleteLeaderNickname: async ({leaderId, nicknameId}: types.DeleteLeaderNicknameRequest): Promise<void> => {
        const {data} = await axios.delete(`/leader/${leaderId}/nickname/${nicknameId}`)
        return data
    },
    uploadMedia: async ({data: body, fileType, options}: types.UploadMediaRequest) => {
        const {data} = await axios.post(`/upload?filetype=${fileType}`, body, {
            ...options,
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        return data
    },
    notifyAdminGoalUpdated: async (goalId: string): Promise<types.GoalType> => {
        const {data} = await axios.post(`/goals/${goalId}/request-review`)
        return data
    },
    withdrawMicroMorgis: async (goalId: string): Promise<types.GoalType> => {
        const {data} = await axios.post(`/goals/${goalId}/retrieve`)
        return data
    }
}

//Saved goals V3
export const getSavedGoals = async (params: types.getSavedGoalsPayloadType): Promise<types.SavedGoalsListType> => {
    const {data} = await axiosV3.get('/goals/saved', {params})
    return data
}

export const saveGoal = async (goalId: string): Promise<GoalV3> => {
    const {data} = await axiosV3.post(`/goals/${goalId}/saved`)
    return data
}

export const unsaveGoal = async (goalId: string) => {
    const {data} = await axiosV3.delete(`/goals/${goalId}/saved`)
    return data
}

//Goals V3
export const getGoalsForCreator = async (): Promise<GoalV3[]> => {
    const {data} = await axiosV3.get('/goals/creator')
    return data
}

export const createGoal = async (body: types.CreationGoalTypeV3): Promise<GoalV3> => {
    const {data} = await axiosV3.post('/goals', body)
    return data
}

export const getGoalForOwner = async ({goalId}: {goalId: string}): Promise<GoalV3> => {
    const {data} = await axiosV3.get(`/goals/${goalId}`)
    return data
}

export const getGoalForFollower = async ({goalId}: {goalId: string}): Promise<GoalV3> => {
    const {data} = await axiosV3.get(`/creators/goals/${goalId}`)
    return data
}

export const getGoalSupporters = async ({goalId}: {goalId: string}): Promise<types.SupporterTypeV3[]> => {
    const {data} = await axiosV3.get(`/goals/${goalId}/supporters`)
    return data
}

export const updateGoal = async ({
    goalId,
    ...body
}: {
    goalId: string
    endDate: Date
    name?: string
    details?: string
    goalMedias?: AttachmentFileType[]
}): Promise<Partial<GoalV3>> => {
    const {data} = await axiosV3.post(`/goals/${goalId}/update`, body)
    return data
}

export const cancelGoal = async ({goalId}: {goalId: string}): Promise<GoalV3> => {
    const {data} = await axiosV3.post(`/goals/${goalId}/cancel`)
    return data
}

export const withdrawGoal = async ({goalId}: {goalId: string}): Promise<GoalV3> => {
    const {data} = await axiosV3.post(`/goals/${goalId}/withdraw`)
    return data
}

export const submitProofForGoal = async ({
    goalId,
    ...body
}: {
    goalId: string
    [PROOF_MODEL.Message]: string
    [PROOF_MODEL.Proofs]: AttachmentFileType[]
    proofsToDelete?: number[]
}): Promise<GoalV3> => {
    const {data} = await axiosV3.post(`/goals/${goalId}/proofs`, body)
    return data
}

export const donateToGoal = async ({
    goalId,
    creatorId,
    ...body
}: {
    goalId: string
    creatorId: string
    micromorgiAmount: number
}): Promise<GoalV3> => {
    const {data} = await axiosV3.post(`/creators/${creatorId}/goals/${goalId}/donate`, body)
    return data
}

export const getTypesOfGoal = async (): Promise<types.GoalSizeTypeV3[]> => {
    const {data} = await axiosV3.get('/goals/types')
    return data
}

export const getHostGoals = async ({hostID}: {hostID: string}): Promise<GoalV3[]> => {
    const {data} = await axiosV3.get(`/creators/${hostID}/goals`)
    return data
}
